import { useMemo, useState } from 'react';
import type { AirportView } from '../../domain/models/contracts';
import type { SimulationControls, SimulationUIState } from '../hooks/useSimulationWorker';
import { SaveMenu } from './SaveMenu';
import './BasePicker.css';

interface BasePickerProps {
  state: SimulationUIState;
  controls: SimulationControls;
}

const MAX_RESULTS = 60;

function matchesQuery(airport: AirportView, query: string): boolean {
  const haystack = `${airport.id} ${airport.name} ${airport.city} ${airport.country}`.toLowerCase();
  return haystack.includes(query);
}

/** Largest runways first, so the major airports lead an empty search. */
function rankAirports(airports: readonly AirportView[], query: string): AirportView[] {
  const needle = query.trim().toLowerCase();
  const filtered = needle === '' ? airports : airports.filter((airport) => matchesQuery(airport, needle));
  return [...filtered]
    .sort((a, b) => b.runwayLengthMeters - a.runwayLengthMeters || a.name.localeCompare(b.name))
    .slice(0, MAX_RESULTS);
}

/** Shown until a company exists: the player picks the airport their airline is based at. */
export function BasePicker({ state, controls }: BasePickerProps) {
  const [query, setQuery] = useState('');
  const results = useMemo(() => rankAirports(state.airports, query), [state.airports, query]);

  return (
    <div className="base-picker">
      <div className="brand base-picker-brand">
        <span className="brand-mark" aria-hidden="true">
          ✈
        </span>
        <span className="brand-name">Flightline</span>
      </div>

      <section className="card base-picker-card" aria-labelledby="base-picker-title">
        <h1 id="base-picker-title">Choose your base</h1>
        <p className="muted">
          Your airline starts with its cash and no aircraft. Pick the airport it is based at; you will acquire
          your first aircraft from the fleet panel.
        </p>

        <label className="base-picker-search">
          <span className="visually-hidden">Search airports</span>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search by city, airport name or IATA code"
            disabled={!state.worldLoaded}
          />
        </label>

        {!state.worldLoaded ? (
          <p className="muted">Loading airports…</p>
        ) : results.length === 0 ? (
          <p className="muted">No airport matches “{query}”.</p>
        ) : (
          <ul className="base-picker-list">
            {results.map((airport) => (
              <li key={airport.id}>
                <button type="button" className="base-picker-option" onClick={() => controls.newGame(airport.id)}>
                  <span className="base-picker-code">{airport.id}</span>
                  <span className="base-picker-name">
                    {airport.name}
                    <span className="muted"> · {airport.city}, {airport.country}</span>
                  </span>
                  <span className="base-picker-runway muted">{airport.runwayLengthMeters.toLocaleString('en-GB')} m</span>
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="base-picker-save">
          <p className="muted">Or continue a saved game:</p>
          <SaveMenu state={state} controls={controls} />
        </div>

        {state.lastError !== null && (
          <p className="base-picker-error" role="alert">
            {state.lastError}
          </p>
        )}
      </section>
    </div>
  );
}
